import React from 'react';

import Icon from '~/src/common/components/Icon';
import I18n from '~/src/common/services/I18n';

import ConditionalTooltip from '../Tooltip';

import * as S from './layout';

interface Props {
  value: number;
  step?: number;
  minimumValue?: number;
  maximumValue: number;
  maximumValueReachedText?: string;
  size?: 'small' | 'large';
  children?: (props: { value: number }) => React.ReactNode;
  onChange: (quantity: number) => void;
}

const NumberPicker = ({
  value,
  step = 1,
  onChange,
  minimumValue = 0,
  maximumValue,
  maximumValueReachedText,
  children,
  size = 'small',
}: Props) => {
  const isMaxQuantityReached = value + step > maximumValue;

  const handlePressRemoveButtonWithTooltip = () => {
    const newValue = value - step;
    onChange(newValue);
  };

  const handlePressAddButtonWithTooltip = () => {
    const newValue = value + step;
    onChange(newValue);
  };

  const buttonSize = size === 'small' ? 24 : 40;
  const iconSize = size === 'small' ? 16 : 24;

  const PlusButton = () => (
    <S.PlusButton
      type="button"
      onClick={handlePressAddButtonWithTooltip}
      disabled={isMaxQuantityReached}
      aria-label={I18n.t('common.add-product')}
      $size={buttonSize}>
      <Icon name="plus" size={iconSize} />
    </S.PlusButton>
  );

  return (
    <S.ButtonsContainer>
      <>
        <S.MinusButton
          type="button"
          aria-label={I18n.t('common.remove-product')}
          onClick={handlePressRemoveButtonWithTooltip}
          disabled={value === minimumValue}
          $size={buttonSize}>
          <Icon name="minus" size={iconSize} />
        </S.MinusButton>
        {typeof children === 'function' ? children({ value }) : <S.Value>{value}</S.Value>}
      </>
      {isMaxQuantityReached && maximumValueReachedText ? (
        <ConditionalTooltip maxWidth={240} content={maximumValueReachedText}>
          <PlusButton />
        </ConditionalTooltip>
      ) : (
        <PlusButton />
      )}
    </S.ButtonsContainer>
  );
};

export default NumberPicker;
